import { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { X, Loader2, Play, Pause, CheckCircle, Barcode } from 'lucide-react';

interface CameraScannerModalProps {
  onScan: (code: string) => Promise<boolean>;
  onClose: () => void;
  scanPending?: boolean;
  scanError?: string;
}

const playBeep = () => {
  try {
    const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 1200; // Slightly higher pitch for barcode
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.start();
    gain.gain.setValueAtTime(1, audioCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.15);
    osc.stop(audioCtx.currentTime + 0.15);
  } catch (err) {
    console.error('Audio beep failed', err);
  }
};

const triggerVibration = () => {
  if ('vibrate' in navigator) {
    navigator.vibrate([50, 50, 50]); // Pattern vibration for barcode
  }
};

export default function CameraScannerModal({ onScan, onClose, scanPending, scanError }: CameraScannerModalProps) {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [autoScan, setAutoScan] = useState(false);
  const [lastScan, setLastScan] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const autoScanRef = useRef(autoScan);
  const lastScanRef = useRef({ code: '', time: 0 });
  const isMountedRef = useRef(true);
  const handleClose = useCallback(async () => {
    if (scannerRef.current?.isScanning) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
      } catch (err) {
        console.error('Failed to stop scanner', err);
      }
    }
    onClose();
  }, [onClose]);

  const onScanRef = useRef(onScan);
  const handleCloseRef = useRef(handleClose);

  useEffect(() => { onScanRef.current = onScan; }, [onScan]);
  useEffect(() => { handleCloseRef.current = handleClose; }, [handleClose]);
  useEffect(() => { autoScanRef.current = autoScan; }, [autoScan]);

  useEffect(() => {
    const initTimer = setTimeout(() => {
      const elementId = 'scanner-viewfinder';
      const scanner = new Html5Qrcode(elementId, {
        verbose: false,
        formatsToSupport: [
          Html5QrcodeSupportedFormats.QR_CODE,
          Html5QrcodeSupportedFormats.EAN_13,
          Html5QrcodeSupportedFormats.EAN_8,
          Html5QrcodeSupportedFormats.UPC_A,
          Html5QrcodeSupportedFormats.UPC_E,
          Html5QrcodeSupportedFormats.CODE_128,
          Html5QrcodeSupportedFormats.CODE_39,
          Html5QrcodeSupportedFormats.CODE_93,
          Html5QrcodeSupportedFormats.DATA_MATRIX,
        ]
      });
      scannerRef.current = scanner;

      const startScanner = async () => {
        try {
          await scanner.start(
            { facingMode: 'environment' },
            {
              fps: 20,
              qrbox: (width: number, height: number) => {
                const minSize = Math.min(width, height);
                // Rectangular box better for barcodes
                const boxWidth = Math.floor(minSize * 0.85);
                const boxHeight = Math.floor(minSize * 0.85);
                return { width: boxWidth, height: boxHeight };
              }
            },
            async (decodedText: string) => {
              if (!isMountedRef.current) return;
              const now = Date.now();
              if (decodedText === lastScanRef.current.code && now - lastScanRef.current.time < 2000) {
                return;
              }
              lastScanRef.current = { code: decodedText, time: now };

              playBeep();
              triggerVibration();
              setLastScan(decodedText);
              setIsProcessing(true);

              const success = await onScanRef.current(decodedText);

              if (!isMountedRef.current) return;
              setIsProcessing(false);
              if (success) {
                if (autoScanRef.current) {
                  // Continuous: clear overlay after 2.5s to prepare for next scan
                  setTimeout(() => {
                    if (isMountedRef.current) setLastScan(null);
                  }, 2500);
                } else {
                  // Single: close modal after 1s so they see success check
                  setTimeout(() => {
                    handleCloseRef.current();
                  }, 1000);
                }
              } else {
                setLastScan(null); // Clear if failed
              }
            },
            () => { }
          );

          if (!isMountedRef.current) {
            await scanner.stop();
            scanner.clear();
            return;
          }
          setIsCameraReady(true);
        } catch (err: any) {
          if (isMountedRef.current) {
            console.error('Scanner error', err);
            setErrorMsg('Failed to open camera. Check permissions.');
          }
        }
      };

      startScanner();
    }, 300);

    return () => {
      clearTimeout(initTimer);
      isMountedRef.current = false;
      if (scannerRef.current) {
        const tempScanner = scannerRef.current;
        if (tempScanner.isScanning) {
          tempScanner.stop().then(() => tempScanner.clear()).catch(() => { });
        }
      }
    };
  }, []);

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 backdrop-blur-md">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col h-[75vh] max-h-[550px]">

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50 shrink-0">
          <div className="flex items-center gap-2 text-primary-600">
            <Barcode className="w-5 h-5" />
            <span className="font-semibold text-gray-900 text-sm">Product Scanner</span>
          </div>
          <div className="flex items-center gap-2">
            {(scanPending || isProcessing) && (
              <div className="flex items-center gap-1.5 text-xs text-primary-600 bg-primary-50 py-1 px-2.5 rounded-full">
                <Loader2 className="w-3 h-3 animate-spin" />
                Adding...
              </div>
            )}
            <button onClick={handleClose} className="p-1.5 hover:bg-gray-200 rounded-xl">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {scanError && (
          <div className="bg-red-50 text-red-700 text-xs font-semibold px-4 py-2.5 border-b border-red-100">
            {scanError}
          </div>
        )}

        {/* Viewfinder */}
        <div className="flex-1 bg-black relative flex items-center justify-center min-h-0 overflow-hidden shrink">
          <div id="scanner-viewfinder" className="absolute inset-0 w-full h-full object-cover [&>video]:w-full [&>video]:h-full [&>video]:object-cover" />

          {!isCameraReady && !errorMsg && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white bg-black/90">
              <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
              <span className="text-xs mt-2">Initializing camera...</span>
            </div>
          )}

          {errorMsg && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white bg-black/95">
              <span className="text-xs text-red-400 font-medium">{errorMsg}</span>
              <button onClick={handleClose} className="mt-4 text-xs border border-white/20 px-4 py-1.5 rounded">
                Close
              </button>
            </div>
          )}

          {/* Scan Success/Processing Overlay */}
          {lastScan && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 z-10 animate-in fade-in">
              {isProcessing ? (
                <>
                  <Loader2 className="w-12 h-12 text-primary-500 animate-spin mb-2" />
                  <span className="text-white text-sm font-semibold">Code: {lastScan}</span>
                  <span className="text-white/60 text-xs mt-1 animate-pulse">Adding to cart...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-12 h-12 text-green-400 mb-2" />
                  <span className="text-white text-sm font-semibold">Code: {lastScan}</span>
                  <span className="text-white text-xs mt-1 text-green-400 font-semibold">Product Added!</span>
                </>
              )}
            </div>
          )}

          {/* Barcode Scan Target Overlay */}
          {isCameraReady && !lastScan && (
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="w-[85%] h-[30%] border-2 border-primary-500/80 rounded-lg relative shadow-[0_0_0_9999px_rgba(0,0,0,0.5)]">
                <div className="absolute inset-x-0 top-1/2 h-0.5 bg-red-500/60 animate-pulse" />
                <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-primary-400" />
                <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-primary-400" />
                <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-primary-400" />
                <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-primary-400" />
              </div>
            </div>
          )}
        </div>

        {/* Bottom controls */}
        <div className="p-4 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-600">Mode:</span>
            <div className="inline-flex border border-gray-200 rounded-lg p-0.5 bg-white">
              <button onClick={() => setAutoScan(true)} className={`py-1 px-3 text-[10px] font-semibold rounded-md ${autoScan ? 'bg-primary-50 text-primary-700' : 'text-gray-500'}`}>
                <Play className="w-2.5 h-2.5 inline mr-1" />Continuous
              </button>
              <button onClick={() => setAutoScan(false)} className={`py-1 px-3 text-[10px] font-semibold rounded-md ${!autoScan ? 'bg-primary-50 text-primary-700' : 'text-gray-500'}`}>
                <Pause className="w-2.5 h-2.5 inline mr-1" />Single
              </button>
            </div>
          </div>
          <span className="text-[10px] text-gray-400">Align barcode or QR inside frame</span>
        </div>
      </div>
    </div>
  );
}